import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProductsService } from 'src/app/_services/products.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { GlobalService } from 'src/app/_services/global.service';
import { ImportJobTrackerService } from 'src/app/_services/import-job-tracker.service';
import { IMPORT_TYPES, ImportIssue, ImportType, ProductIssueGroup, groupIssues, validateCsv } from './import-rules';

const FINISHED_STATUSES = ['Completed', 'CompletedWithErrors', 'Failed', 'Stalled'];

@Component({
  selector: 'app-import-v2',
  templateUrl: './import-v2.component.html',
  styleUrls: ['./import-v2.component.scss']
})
export class ImportProductsV2Component implements OnInit, OnDestroy {
  _globals = GlobalService;
  types = IMPORT_TYPES;
  importType: ImportType = 'quick';
  private readonly typeKey = 'importProductsType';
  importOption = 'skip';
  selectedLocation: any;
  activeLocations: any;
  file: File;
  rowCount = 0;
  issues: ImportIssue[] = [];
  groups: ProductIssueGroup[] = [];
  readonly maxShown = 100;
  validating = false;
  importStatus = '';
  importMessage = '';
  apiIssues: any[] = [];
  readonly maxFileSize = 5000000;
  job: any = null;
  jobIssues: any[] = [];
  private jobSub: Subscription;

  constructor(
    public _productsService: ProductsService,
    public _abd: AppBaseDataService,
    private _router: Router,
    private _tracker: ImportJobTrackerService
  ) { }

  ngOnInit(): void {
    this._globals.componentName = 'Inventory Import';
    this.activeLocations = this._abd.initActLocations();
    try {
      const saved = sessionStorage.getItem(this.typeKey);
      if (saved === 'quick' || saved === 'detailed') { this.importType = saved; }
    } catch (e) { /* storage unavailable: keep default */ }

    // A job may still be running from an earlier visit: show its progress again.
    const running = this._tracker.running$.value;
    if (running) {
      this.importStatus = 'started';
      this.job = running;
    }
    this.jobSub = this._tracker.updates$.subscribe((j: any) => this.onJobUpdate(j));
  }

  ngOnDestroy(): void {
    this._globals.componentName = '';
    this.jobSub?.unsubscribe();
  }

  /** Browser "Leave site?" prompt on close/reload while the upload is still being sent. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    // Once the job id is returned the import keeps running on the server.
    if (this.importStatus !== 'started' || this.job) { return; }
    event.preventDefault();
    event.returnValue = true;
    return 'Import is still running. Leaving will cancel it.';
  }

  setType(t: ImportType) {
    if (this.importType === t) { return; }
    this.importType = t;
    if (t === 'quick' && this.importOption === 'updateOnlyStocks') { this.importOption = 'skip'; }
    try { sessionStorage.setItem(this.typeKey, t); } catch (e) { /* ignore */ }
    this.clear();
  }

  private startQuick() {
    this.startJob(this._productsService.importQuick(this.file, this.importMode(), this.importLocationId()));
  }

  private startDetail() {
    this.startJob(this._productsService.importDetail(this.file, this.importMode(), this.importLocationId()));
  }

  private importMode() {
    return this.importOption === 'updateProducts' ? 'overwrite'
      : this.importOption === 'updateOnlyStocks' ? 'stockOnly' : 'skip';
  }

  private importLocationId() {
    return this.activeLocations.length > 1
      ? (this.selectedLocation && this.selectedLocation.id)
      : (this.activeLocations[0] && this.activeLocations[0].id);
  }

  private startJob(request: any) {
    request.subscribe(
      (res: any) => {
        const jobId = res && res.data && res.data.jobId;
        if (!res || res.success === false || !jobId) {
          this.failImport((res && res.message) || 'Import could not be started.');
          return;
        }
        this.job = { jobId, status: 'Queued', totalRows: res.data.totalRows, processedRows: 0, percentComplete: 0 };
        this._tracker.track(jobId, res.data.totalRows);
      },
      (error: any) => this.failImport(this.errorText(error))
    );
  }

  /** Progress and the final result come from the tracker, so they also arrive after the user left and came back. */
  private onJobUpdate(j: any) {
    if (!this.job || !j || j.jobId !== this.job.jobId) { return; }
    this.job = j;
    if (FINISHED_STATUSES.indexOf(j.status) >= 0) { this.finishQuick(j); }
  }

  private finishQuick(j: any) {
    this.importMessage = j.status === 'Stalled'
      ? 'The import stopped making progress (usually a server restart). Upload the file again; existing names are skipped.'
      : (j.message || '');
    this.jobIssues = j.issues || [];
    this.importStatus = j.status === 'Completed' || j.status === 'CompletedWithErrors' ? 'completed' : 'error';
  }

  private failImport(message: string) {
    this.importMessage = message;
    this.importStatus = 'error';
  }

  private errorText(error: any) {
    return (error && (error.message || (error.error && error.error.message)))
      || (typeof error === 'string' ? error : 'Import failed. Please try again.');
  }

  goBack() {
    this._router.navigate(['app/products-v2']);
  }

  get cfg() { return this.types[this.importType]; }
  get canUpload() { return !!this.file && !this.validating && this.issues.length === 0; }

  onFileSelect(event: any) {
    const f: File = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!f) { return; }
    this.file = f;
    this.runValidation();
  }

  isDragOver = false;

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (!this.file) { this.isDragOver = true; }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const f: File = event.dataTransfer?.files?.[0];
    if (!f || this.file) { return; }
    this.file = f;
    this.runValidation();
  }

  clear() {
    this.file = null;
    this.setIssues([]);
    this.rowCount = 0;
  }

  private setIssues(issues: ImportIssue[]) {
    this.issues = issues;
    this.groups = groupIssues(issues);
  }

  private fileIssue(message: string, fix: string): ImportIssue[] {
    return [{ row: 0, product: 'File', column: '', value: '', message, fix }];
  }

  private runValidation() {
    const f = this.file;
    this.setIssues([]);
    if (!/\.csv$/i.test(f.name)) {
      this.setIssues(this.fileIssue('Only .csv files are allowed.', 'In Excel choose Save As > "CSV UTF-8 (Comma delimited)".'));
      return;
    }
    if (f.size > this.maxFileSize) {
      this.setIssues(this.fileIssue('File is larger than 5MB.', 'Split the products into smaller files.'));
      return;
    }
    this.validating = true;
    const reader = new FileReader();
    reader.onload = () => {
      const res = validateCsv(reader.result as string, this.cfg);
      this.setIssues(res.issues);
      this.rowCount = res.rowCount;
      this.validating = false;
    };
    reader.onerror = () => {
      this.setIssues(this.fileIssue('Could not read the file.', 'Close it in other programs and choose it again.'));
      this.validating = false;
    };
    reader.readAsText(f);
  }

  upload() {
    if (!this.canUpload) { return; }
    this.importStatus = 'started';
    this.importMessage = '';
    this.apiIssues = [];
    this.job = null;
    this.jobIssues = [];
    if (this.importType === 'quick') { this.startQuick(); } else { this.startDetail(); }
  }

  reset(keepFile = false) {
    this.importStatus = '';
    this.importMessage = '';
    this.apiIssues = [];
    this.job = null;
    this.jobIssues = [];
    if (!keepFile) { this.clear(); }
  }

  apiIssueText(i: any) {
    return typeof i === 'string' ? i : (i.message || i.error || i.skuCode || JSON.stringify(i));
  }

  gotoaddlocation() {
    this._router.navigate(['app/locations-list']);
  }
}
